import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button, TextField, Typography, Container, Box, Grid2 as Grid, Paper, CircularProgress } from '@mui/material';
import { useSnackbar } from '@contexts/SnackbarContext';
import API from '@utils/axios';
import LangSelect from '@components/LangSelect';
import PasswordField from '@components/PasswordField';
import { isPasswordValid } from '@utils/passwordValidation';

function ResetPasswordPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    password: '',
    password_confirmation: '',
    token: searchParams.get('token') || '',
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Vérifier si les mots de passe correspondent
    if (formData.password !== formData.password_confirmation) {
      showMessage(t('Passwords do not match'), 'error');
      return;
    }
    
    // Vérifier si le mot de passe respecte toutes les règles
    if (!isPasswordValid(formData.password)) {
      showMessage(t('Password does not meet the requirements'), 'error');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await API.post('/auth/password/reset', formData);
      showMessage(t(response.data.message), 'success');
      navigate('/login');
    } catch (err) {
      showMessage(
        err.response?.data?.message ? t(err.response.data.message) : err.message,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ width: '100%', p: 4, mt: { xs: 2, sm: 6, md: 12 } }}>
        <img src="/logo.webp" alt="Logo" style={{ width: '100%', height: 'auto', marginBottom: 16 }} />

        <Grid container>
          <Grid size="grow" xs>
            <Typography component="h1" variant="h2">
              {t("Reset Password")}
            </Typography>
          </Grid>
          <Grid>
            <LangSelect
              variant="standard"
              value={i18n.language}
              onChange={handleLanguageChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Language' }}
            />
          </Grid>
        </Grid>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            label={t("Email Address")}
            autoComplete="email"
            value={formData.email}
            onChange={handleChange('email')}
          />
          <Box sx={{ mt: 2, mb: 2 }}>
            <PasswordField
              field={{
                value: formData.password,
                onChange: (e) => handleChange('password')(e),
                name: 'password',
              }}
              label={t("New Password")}
            />
          </Box>
          <Box sx={{ mt: 2, mb: 2 }}>
            <PasswordField
              field={{
                value: formData.password_confirmation,
                onChange: (e) => handleChange('password_confirmation')(e),
                name: 'password_confirmation',
              }}
              label={t("Confirm New Password")}
              showRules={false}
            />
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('Validate')
            )}
          </Button>
          <Grid container justifyContent="center">
            <Grid>
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary">
                  {t('Cancel')}
                </Typography>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
}

export default ResetPasswordPage; 